import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import type { IRGB } from '@picsart/web-layering/types';
import type { Credits } from '@picsart/miniapp-sdk-types';
import type { SuggestedBgResult } from 'services/pluggableApis/AIBackgroundsApis/types';
import type { IBatchData, IUserBackground, ICropAttrsBatch } from 'types';
import type { ICropTemplateValue } from 'pages/Batch/types';
import type { ObjectPosition } from 'pages/Batch/sections/Sidebar/EditorRemoveBgTool/MobilePanel/constants';
import type { IBgToolRatio, ICropFlip, ICustomImage, IBatchState, IForegroundTransform, IForegroundContentBbox, IWatermarkItemSetting, IBackgroundItemSetting, IResizeItemSetting } from 'types/store';

import type { IWfxEffect } from 'pages/Batch/sections/Sidebar/WfxTool/constants';
import initialState from './initialState';

const batchPersistConfig = {
  key: 'batch',
  storage,
  // Persist the export paywall gate so it survives a reload, in lockstep with the
  // edited images (which are restored on desktop). Reset on reset-all, delete-all,
  // and the mobile data wipe so it never lingers without premium edits.
  whitelist: ['hasUsedSubscriptionTool'] as string[],
};

const batchSlice = createSlice({
  name: 'batch',
  initialState,
  reducers: {
    setEraserItem(state, { payload }: PayloadAction<IBatchData | null>) {
      state.eraserItem = payload;
    },
    setActiveCustomBackground(state, { payload }: PayloadAction<IUserBackground | null>) {
      state.activeCustomBg = payload;
    },
    setObjectPosition(state, { payload }: PayloadAction<ObjectPosition | null>) {
      state.objectPosition = payload;
    },
    setBgToolRatio(state, { payload }: PayloadAction<IBgToolRatio | null>) {
      state.bgToolRatio = payload;
      state.foregroundTransforms = {};
    },
    setForegroundTransform(state, { payload }: PayloadAction<{ key: string; transform: IForegroundTransform }>) {
      state.foregroundTransforms[payload.key] = payload.transform;
    },
    resetForegroundTransform(state, { payload }: PayloadAction<string>) {
      delete state.foregroundTransforms[payload];
    },
    setForegroundContentBbox(state, { payload }: PayloadAction<{ key: string; bbox: IForegroundContentBbox }>) {
      state.foregroundContentBboxes[payload.key] = payload.bbox;
    },
    setActiveColor(state, { payload }: PayloadAction<IRGB | null>) {
      state.activeColor = payload;
    },
    // Per-item settings snapshots (see IWatermarkItemSetting et al.): Apply bakes each item's own
    // previewed state. Written for the live selection on every change, read per item on Apply.
    setWatermarkItemSetting(state, { payload }: PayloadAction<{ key: string; setting: IWatermarkItemSetting }>) {
      state.watermarkItemSettings[payload.key] = payload.setting;
    },
    clearWatermarkItemSetting(state, { payload }: PayloadAction<string>) {
      delete state.watermarkItemSettings[payload];
    },
    resetWatermarkItemSettings(state) {
      state.watermarkItemSettings = initialState.watermarkItemSettings;
    },
    setBackgroundItemSetting(state, { payload }: PayloadAction<{ key: string; setting: IBackgroundItemSetting }>) {
      state.backgroundItemSettings[payload.key] = payload.setting;
    },
    resetBackgroundItemSettings(state) {
      state.backgroundItemSettings = initialState.backgroundItemSettings;
    },
    setResizeItemSetting(state, { payload }: PayloadAction<{ key: string; setting: IResizeItemSetting }>) {
      state.resizeItemSettings[payload.key] = payload.setting;
    },
    resetResizeItemSettings(state) {
      state.resizeItemSettings = initialState.resizeItemSettings;
    },
    setSuggestedBackgrounds(state, { payload }: PayloadAction<SuggestedBgResult[]>) {
      state.suggestedBackgrounds = payload?.length ? [...state.suggestedBackgrounds, ...payload] : [];
    },
    setGeneratedAIBackgrounds(state, { payload }: PayloadAction<{ id: string; url: string }[]>) {
      payload.forEach(({ id, url }) => {
        const index = state.generatedBackgrounds.findIndex(item => item.id === id);
        if (index > -1) {
          state.generatedBackgrounds[index].url = url;
        } else {
          state.generatedBackgrounds.push({ id, url });
        }
      });
    },
    clearGeneratedAIBackgrounds(state, { payload }: PayloadAction<string[]>) {
      state.generatedBackgrounds = payload.length ? state.generatedBackgrounds.filter(item => !payload.includes(item.id)) : [];
    },
    setLastAIBGPrompt(state, { payload }: PayloadAction<string | null>) {
      state.lastAIBGPrompt = payload;
    },
    resetBackgroundState(state) {
      state.activeCustomBg = null;
      state.activeColor = null;
      state.objectPosition = null;
      state.bgToolRatio = null;
      state.generatedBackgrounds = [];
      state.lastAIBGPrompt = null;
      state.foregroundTransforms = {};
      state.foregroundContentBboxes = {};
      state.backgroundItemSettings = {};
    },
    setPreviewCollection(state, { payload }: PayloadAction<{ id: string; item: IBatchData }>) {
      const { id, item } = payload;
      const currentItem = state.previewCollection[`${id}`];
      if (currentItem) {
        currentItem.items.push(item);
      } else {
        state.previewCollection[`${id}`] = {
          items: [item],
        };
      }
    },

    setPreviewItemPrompt(state, { payload }: PayloadAction<{ id: string; prompt: string }>) {
      const { id, prompt } = payload;

      state.previewCollection[`${id}`].prompt = prompt;
    },
    resetPreviewCollection(state) {
      state.previewCollection = {};
    },
    setActivePreviewItem(state, { payload }: PayloadAction<IBatchData | null>) {
      state.activePreviewItem = payload;
    },
    setCreditsData(state, { payload }: PayloadAction<Credits>) {
      state.creditsData = payload;
    },
    setToolPrice(state, { payload }: PayloadAction<number>) {
      state.toolPrice = payload;
    },
    setCropTemplate(state, { payload }: PayloadAction<ICropTemplateValue>) {
      state.cropTemplate = payload;
    },
    setCropOrientation(state, { payload }: PayloadAction<string>) {
      state.cropOrientation = payload;
    },
    setCropAlign(state, { payload }: PayloadAction<string | null>) {
      state.cropAlign = payload;
    },
    setCropRotate(state, { payload }: PayloadAction<number>) {
      state.cropRotate = payload;
    },
    setCropFlip(state, { payload }: PayloadAction<ICropFlip>) {
      state.cropFlip = payload;
    },
    updateCropAttrs(state, { payload }: PayloadAction<ICropAttrsBatch>) {
      const currentItemIndex = state.cropAttrs.findIndex(item => item.key === payload.key);
      if (currentItemIndex > -1) {
        state.cropAttrs[currentItemIndex] = payload;
      } else {
        state.cropAttrs.push(payload);
      }
    },
    setEnhanceMapping(state, { payload }: PayloadAction<Record<string, string>>) {
      state.enhanceMapping = { ...state.enhanceMapping, ...payload };
    },
    resetEnhanceMapping(state) {
      state.enhanceMapping = initialState.enhanceMapping;
    },
    resetEnhanceItemIntensities(state) {
      state.enhanceItemIntensities = initialState.enhanceItemIntensities;
    },
    setEnhanceItemIntensity(state, { payload }: PayloadAction<{ key: string; intensity: number }>) {
      state.enhanceItemIntensities[payload.key] = payload.intensity;
    },
    setAllEnhanceItemIntensities(state, { payload }: PayloadAction<number>) {
      Object.keys(state.enhanceItemIntensities).forEach(key => {
        state.enhanceItemIntensities[key] = payload;
      });
    },
    setUserWatermarks(state, { payload }: PayloadAction<ICustomImage>) {
      state.userWatermarks = [...state.userWatermarks, payload];
    },
    deleteWatermark(state, { payload }: PayloadAction<string>) {
      state.userWatermarks = state.userWatermarks.filter(el => el.id !== payload);
    },
    setBackgroundCustomImage(state, { payload }: PayloadAction<ICustomImage>) {
      state.backgroundCustomImages = [payload, ...state.backgroundCustomImages.slice(0, payload.isWebScreen ? 7 : 6)];
    },
    deleteBackgroundCustomImage(state, { payload }: PayloadAction<string>) {
      state.backgroundCustomImages = state.backgroundCustomImages.filter(el => el.id !== payload);
    },
    setEnhanceBeforeAfter(state, { payload }: PayloadAction<number | null>) {
      state.enhanceBeforeAfter = payload;
    },
    setLatestAppliedTool(state, { payload }: PayloadAction<string | null>) {
      state.latestAppliedTool = payload;
    },
    setHasUsedSubscriptionTool(state, { payload }: PayloadAction<boolean>) {
      state.hasUsedSubscriptionTool = payload;
    },
    setIsToolApplying(state, { payload }: PayloadAction<boolean>) {
      state.isToolApplying = payload;
    },
    setWfxSelectedEffect(state, { payload }: PayloadAction<IWfxEffect | null>) {
      state.wfxSelectedEffect = payload;
    },
    setWfxActiveTab(state, { payload }: PayloadAction<'effects' | 'overlays'>) {
      state.wfxActiveTab = payload;
    },
    setWfxSelectedOverlay(state, { payload }: PayloadAction<string | null>) {
      state.wfxSelectedOverlay = payload;
    },
    setWfxShowSettings(state, { payload }: PayloadAction<boolean>) {
      state.wfxShowSettings = payload;
    },
    setWfxParamValue(state, { payload }: PayloadAction<{ name: string; value: number }>) {
      state.wfxParamValues[payload.name] = payload.value;
    },
    setWfxParamValues(state, { payload }: PayloadAction<Record<string, number>>) {
      state.wfxParamValues = payload;
    },
    setWfxProcessAttrs(state, { payload }: PayloadAction<IBatchState['wfxProcessAttrs']>) {
      state.wfxProcessAttrs = payload;
    },
    setAdjustProcessAttrs(state, { payload }: PayloadAction<IBatchState['adjustProcessAttrs']>) {
      state.adjustProcessAttrs = payload;
    },
    setAiEditParams(state, { payload }: PayloadAction<IBatchState['aiEditParams']>) {
      state.aiEditParams = payload;
    },
    setEnhanceSelectedSmartAdjustment(state, { payload }: PayloadAction<string | null>) {
      state.enhanceSelectedSmartAdjustment = payload;
    },
    setEnhanceSelectedBackgroundBlur(state, { payload }: PayloadAction<string | null>) {
      state.enhanceSelectedBackgroundBlur = payload;
    },
    setEnhanceSmartAdjustmentIntensity(state, { payload }: PayloadAction<number>) {
      state.enhanceSmartAdjustmentIntensity = payload;
    },
    setEnhanceBackgroundBlurIntensity(state, { payload }: PayloadAction<number>) {
      state.enhanceBackgroundBlurIntensity = payload;
    },
    setEnhanceActiveSection(state, { payload }: PayloadAction<'smartAdjustment' | 'backgroundBlur' | null>) {
      state.enhanceActiveSection = payload;
    },
    setEnhanceActiveSubtool(state, { payload }: PayloadAction<string | null>) {
      state.enhanceActiveSubtool = payload;
    },
    resetEnhanceEffectsState(state) {
      state.enhanceSelectedSmartAdjustment = initialState.enhanceSelectedSmartAdjustment;
      state.enhanceSelectedBackgroundBlur = initialState.enhanceSelectedBackgroundBlur;
      state.enhanceSmartAdjustmentIntensity = initialState.enhanceSmartAdjustmentIntensity;
      state.enhanceBackgroundBlurIntensity = initialState.enhanceBackgroundBlurIntensity;
      state.enhanceActiveSection = initialState.enhanceActiveSection;
    },
    resetAdjustState(state) {
      state.adjustProcessAttrs = initialState.adjustProcessAttrs;
    },
    resetWfxState(state) {
      state.wfxSelectedEffect = initialState.wfxSelectedEffect;
      state.wfxActiveTab = initialState.wfxActiveTab;
      state.wfxSelectedOverlay = initialState.wfxSelectedOverlay;
      state.wfxShowSettings = initialState.wfxShowSettings;
      state.wfxParamValues = initialState.wfxParamValues;
      state.wfxProcessAttrs = initialState.wfxProcessAttrs;
    },
    reset: () => initialState,
  },
});

export default batchSlice;

export const persistedBatchReducer = persistReducer(batchPersistConfig, batchSlice.reducer);
